import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

const fila = [];
let notificarNovoPopup = null;

function enfileirarPopup(configuracao) {
  return new Promise((resolve) => {
    fila.push({ ...configuracao, resolve });
    if (notificarNovoPopup) notificarNovoPopup();
  });
}

export function alertaSistema(mensagem, opcoes = {}) {
  return enfileirarPopup({ tipo: "alerta", mensagem: String(mensagem ?? ""), ...opcoes });
}

export function confirmarSistema(mensagem, opcoes = {}) {
  return enfileirarPopup({ tipo: "confirmacao", mensagem: String(mensagem ?? ""), ...opcoes });
}

export function solicitarTextoSistema(mensagem, valorInicial = "", opcoes = {}) {
  return enfileirarPopup({ tipo: "texto", mensagem: String(mensagem ?? ""), valorInicial: String(valorInicial ?? ""), ...opcoes });
}

export function registrarConfirmacaoCancelamento(confirmar = confirmarSistema) {
  const autorizados = new WeakSet();
  let pendente = false;
  let ativo = true;

  async function verificarCancelamento(event) {
    const botao = event.target?.closest?.('button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"], a');
    if (!botao || botao.disabled || botao.getAttribute("aria-disabled") === "true" ||
        botao.closest('[data-confirmacao-cancelamento="propria"]')) return;
    if (autorizados.has(botao)) return;
    const rotulos = [botao.textContent, botao.value, botao.getAttribute("aria-label"), botao.getAttribute("title")];
    if (!rotulos.some((rotulo) => /^cancelar\b/i.test(String(rotulo || "").trim()))) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendente) return;
    pendente = true;
    try {
      const aceito = await confirmar("Tem certeza que deseja cancelar as alterações?", {
        titulo: "Cancelar alterações",
        textoConfirmar: "Sim, cancelar",
        textoCancelar: "Continuar editando",
      });
      // Aguarda o fechamento do popup antes de executar o clique original.
      await new Promise((resolve) => window.setTimeout(resolve, 0));
      if (aceito && ativo && botao.isConnected && !botao.disabled && botao.getAttribute("aria-disabled") !== "true") {
        autorizados.add(botao);
        try { botao.click(); } finally { autorizados.delete(botao); }
      }
    } finally {
      pendente = false;
    }
  }

  document.addEventListener("click", verificarCancelamento, true);
  return () => {
    ativo = false;
    document.removeEventListener("click", verificarCancelamento, true);
  };
}

export default function PopupSistema() {
  const [popup, setPopup] = useState(null);
  const [valor, setValor] = useState("");
  const popupRef = useRef(null);

  useEffect(() => registrarConfirmacaoCancelamento(), []);

  const abrirProximo = useCallback(() => {
    if (popupRef.current || fila.length === 0) return;
    const proximo = fila.shift();
    popupRef.current = proximo;
    setValor(proximo.valorInicial || "");
    setPopup(proximo);
  }, []);

  useEffect(() => {
    notificarNovoPopup = abrirProximo;
    abrirProximo();
    const alertaNativo = window.alert;
    window.alert = (mensagem) => alertaSistema(mensagem);
    return () => {
      notificarNovoPopup = null;
      window.alert = alertaNativo;
    };
  }, [abrirProximo]);

  function concluir(resultado) {
    const atual = popupRef.current;
    popupRef.current = null;
    setPopup(null);
    if (atual) atual.resolve(resultado);
    window.setTimeout(abrirProximo, 0);
  }

  function confirmar() {
    if (popup?.tipo === "texto") concluir(valor);
    else if (popup?.tipo === "confirmacao") concluir(true);
    else concluir(undefined);
  }

  function cancelar() {
    concluir(popup?.tipo === "texto" ? null : false);
  }

  return (
    <Dialog data-confirmacao-cancelamento="propria" open={Boolean(popup)} onClose={popup?.tipo === "alerta" ? undefined : cancelar} maxWidth="xs" fullWidth aria-labelledby="popup-sistema-titulo">
      <DialogTitle id="popup-sistema-titulo">
        {popup?.titulo || (popup?.tipo === "confirmacao" ? "Confirmação" : "Atenção")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: "pre-line" }}>{popup?.mensagem}</DialogContentText>
        {popup?.tipo === "texto" && (
          <TextField autoFocus fullWidth value={valor} onChange={(event) => setValor(event.target.value)} onKeyDown={(event) => event.key === "Enter" && confirmar()} sx={{ mt: 2 }} />
        )}
      </DialogContent>
      <DialogActions>
        {popup?.tipo !== "alerta" && <Button onClick={cancelar}>{popup?.textoCancelar || "Cancelar"}</Button>}
        <Button variant="contained" onClick={confirmar} autoFocus={popup?.tipo !== "texto"}>
          {popup?.textoConfirmar || (popup?.tipo === "alerta" ? "OK" : "Confirmar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
